import { useRef, useState, useEffect } from "react";

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

const partners = [
  {
    name: "Solana Vibe Station",
    desc: "RPC provider powering RYS infrastructure",
    link: "https://cloud.solanavibestation.com/?affid=31",
    logo: "https://framerusercontent.com/images/NDeadXA3IQnY9l0KHG3rBxlQvf0.png",
  },
  {
    name: "Stryke Trading Bot",
    desc: "Fast, MEV-protected Solana trading bot",
    link: "https://t.me/stryke_trading_bot?start=ref_RYS",
    logo: "/project/assets/stryke_white_transparent.png",
  },
  {
    name: "SolHouse Signal",
    desc: "12K+ subscribers Solana alpha channel",
    link: "https://t.me/solhousesignal",
    logo: "/project/assets/solhousesignal.jpg",
  },
];

const listings = [
  { 
    name: "CoinMarketCap", 
    desc: "Listed", 
    link: "https://coinmarketcap.com/currencies/refundyoursol/",
    logo: "/project/assets/cmc-logo.png",
  },
  { 
    name: "CoinGecko", 
    desc: "Verified", 
    link: "https://www.coingecko.com/en/coins/refundyoursol",
    logo: "/project/assets/coingecko-logo.png",
  },
  { 
    name: "Jupiter", 
    desc: "Verified", 
    link: "https://jup.ag/tokens/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump",
    logo: "/project/assets/jupiter-logo.png",
  },
  { 
    name: "Solscan", 
    desc: "Verified", 
    link: "https://solscan.io/account/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump",
    logo: "/project/assets/solscan-logo.png",
  },
  { 
    name: "Phantom", 
    desc: "dApp", 
    link: "https://phantom.com/apps/refundyoursol",
    logo: "/project/assets/phantom-logo.png",
  },
];

const ListingCard = ({ item, index }) => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <a
      ref={ref}
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`glass rounded-xl px-5 py-3 flex items-center gap-3 hover:bg-white/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group border border-white/5 hover:border-purple-500/20 transform-gpu ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
      }`}
      style={{ 
        transitionDelay: `${index * 0.05}s`,
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
        {item.logo ? (
          <img 
            src={item.logo} 
            alt={item.name}
            className="w-8 h-8 object-contain"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              // Fallback to initials if logo fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<span class="text-purple-400 font-bold text-xs">${item.name.slice(0, 2)}</span>`;
            }}
          />
        ) : (
          <span className="text-purple-400 font-bold text-xs">{item.name.slice(0, 2)}</span>
        )}
      </div>
      <div className="text-left">
        <span className="text-white text-sm font-medium group-hover:text-purple-300 transition-colors">{item.name}</span>
        <p className="text-green-500 text-xs">{item.desc}</p>
      </div>
    </a>
  );
};

const PartnerCard = ({ partner, index }) => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <a
      ref={ref}
      href={partner.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative glass rounded-2xl p-6 flex flex-col items-center text-center overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-500 border border-white/5 hover:border-purple-500/30 transform-gpu hover:-translate-y-1 hover:scale-[1.02] ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ 
        transitionDelay: `${index * 0.1}s`,
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      {/* Gradient glow on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      {/* Logo */}
      <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-4 overflow-hidden group-hover:bg-white/20 transition-colors">
        <img 
          src={partner.logo} 
          alt={partner.name}
          className="w-12 h-12 object-contain"
          loading="lazy"
          decoding="async"
        />
      </div>
      
      <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors">
        {partner.name}
      </h4>
      <p className="text-gray-400 text-sm">{partner.desc}</p>
    </a>
  );
};

export default function Partners() {
  const headingRef = useRef(null);
  const headingVisible = useIsVisible(headingRef, { once: true });

  return (
    <section id="partners" className="relative py-24 bg-[#0b0b0f] border-t border-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(168,85,247,0.08),transparent_60%)]" />
      
      <div className="relative max-w-6xl mx-auto px-6">
        <div
          ref={headingRef}
          className={`text-center mb-16 transition-all duration-600 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ WebkitBackfaceVisibility: 'hidden' }}
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            Partners & Integrations
          </h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Working with leading projects in the Solana ecosystem
          </p>
        </div>

        {/* Partners Grid */}
        <div className="mb-16">
          <h4 className="text-lg font-semibold text-gray-300 mb-6 text-center">Strategic Partners</h4>
          <div className="grid gap-6 sm:grid-cols-3 max-w-3xl mx-auto">
            {partners.map((partner, i) => (
              <PartnerCard key={partner.name} partner={partner} index={i} />
            ))}
          </div>
        </div>

        {/* Listings */}
        <div>
          <h4 className="text-lg font-semibold text-gray-300 mb-6 text-center">Listed & Verified On</h4>
          <div className="flex flex-wrap justify-center gap-4">
            {listings.map((item, i) => (
              <ListingCard key={item.name} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
